import { useMemo, useState } from 'react';
import { history } from 'umi';
import { useMemoizedFn, useAsyncEffect } from 'ahooks';
import { omit } from 'lodash-es';
import { fetchDPSReportsSummary, deleteDPSReport, updateDPSReportPublishState } from '@gp/services/dpsReport/common';
import { fetchDPSReportDetails as fetchDPSReportDetailsV2 } from '@gp/services/dpsReport/v2';
import { fetchDPSReportDetails as fetchDPSReportDetailsV3 } from '@gp/services/dpsReport/v3';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import { reportBtnOperationLog, messageTips } from '@gp/utils/common';
// @ts-ignore
import { storage } from '@gp/utils/utils';
import { getDPSReports } from '../utils';
import { formatDPSReport as formatDPSReportV2 } from '../v2/utils';
import { formatDPSReport as formatDPSReportV3 } from '../v3/utils';
import { BoolFlag } from '@gp/enums/common';
import { SLASH_G_POP, CONSTANT_TEXTS } from '@gp/constants/const';
import { DPS_REPORT_STORAGE_KEY } from '@gp/constants/storageKeys';

import type { SelectOptionWithGroup } from 'tea-component';
import type { IDPSReportBaseVO } from '@gp/types/dpsReport/common';
import type { IDPSReportVOV2 } from '@gp/types/dpsReport/v2';
import type { IDPSReportVOV3 } from '@gp/types/dpsReport/v3';

const defaultFuncs = {
  formatDPSReport: formatDPSReportV2,
  fetchDPSReportDetails: fetchDPSReportDetailsV2,
};

const funcsMapping = {
  '1': defaultFuncs,
  '2': defaultFuncs,
  '3': {
    formatDPSReport: formatDPSReportV3,
    fetchDPSReportDetails: fetchDPSReportDetailsV3,
  },
};

export default function useDPSReport() {
  const [isLoading, setIsLoading] = useState(true);
  const [isReportLoading, setIsReportLoading] = useState(true);
  const [report, setReport] = useState<IDPSReportVOV2 | IDPSReportVOV3 | null>(null);
  const [reports, setReports] = useState<IDPSReportBaseVO[]>([]);

  const reportOptions = useMemo<SelectOptionWithGroup[]>(() => {
    if (!Array.isArray(reports) || !reports.length) return [];
    return reports.map((reportWeek) => {
      const { id } = reportWeek || {};
      return {
        text: id,
        value: id,
      };
    });
  }, [reports]);

  const updateReport = async (payload?: string) => {
    setIsReportLoading(true);
    try {
      const report = reports?.find((report) => report?.id === payload);
      if (!report) throw new Error('updateCurrentReport report is not matched');
      const reportTmp = omit(report, [
        'year',
        'weeks_in_year',
        'start_date_dps_report_week',
        'end_date_dps_report_week',
      ]);
      const { formatDPSReport, fetchDPSReportDetails } = funcsMapping[reportTmp?.type] || defaultFuncs;
      const { data } = await fetchDPSReportDetails(reportTmp);
      const { start_date: startDate, end_date: endDate } = data || {};
      setReport(formatDPSReport(data as any));
      updateURLSearchParams({ start_date: startDate || '', end_date: endDate || '' });
    } catch (exception) {
      setReport(null);
    } finally {
      setIsReportLoading(false);
    }
  };

  const handleDeleteReport = async (report: IDPSReportBaseVO) => {
    const { type, id, report_id: reportId } = report || {};
    await deleteDPSReport({ type, id, report_id: reportId });
    const reportsTmp = reports?.filter((item) => item?.id !== id) || [];
    setReports(reportsTmp);
  };

  const handleAddReport = () => {
    storage.setSessionStorageItem(DPS_REPORT_STORAGE_KEY.DPS_REPORT_REPORTS_SUMMARY_STORAGE_KEY, reports);
    history.push(`${SLASH_G_POP}/Pipeline/DPSNewsletter/AddReport`);
  };

  const handleEditReport = (title: string) => {
    if (!report) return;
    storage.setSessionStorageItem(DPS_REPORT_STORAGE_KEY.DPS_REPORT_REPORT_TITLE, title);
    storage.setSessionStorageItem(DPS_REPORT_STORAGE_KEY.DPS_REPORT_REPORT_DETAILS, report);
    history.push(`${SLASH_G_POP}/Pipeline/DPSNewsletter/EditReport`);
  };

  const handlePublishReport = useMemoizedFn(async (payload: BoolFlag) => {
    if (!report) return;
    const { type, id, report_id: reportId } = report || {};
    reportBtnOperationLog(
      payload === BoolFlag.True
        ? 'WORKSPACE_DPS_WEEKLY_REPORT_PUBLISH_SUBMIT'
        : 'WORKSPACE_DPS_WEEKLY_REPORT_UNPUBLISH_SUBMIT',
    );
    try {
      await updateDPSReportPublishState({
        type: type || '',
        id: id || '',
        report_id: reportId || '',
        is_publish: payload,
      });
      const reportTmp = {
        ...report,
        is_publish: payload,
      };
      setReport(reportTmp);
      messageTips('success', CONSTANT_TEXTS.OPERATION_SUCCESS_TIPS);
    } catch (exception) {
      messageTips('error', CONSTANT_TEXTS.OPERATION_FAILURE_TIPS);
    }
  });

  useAsyncEffect(async () => {
    try {
      setIsLoading(true);
      const res = await fetchDPSReportsSummary();
      setReports(getDPSReports(res?.data?.list));
    } catch (exception) {
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    isReportLoading,
    report,
    reports,
    reportOptions,
    updateReport,
    onAddReport: handleAddReport,
    onEditReport: handleEditReport,
    onPublishReport: handlePublishReport,
    onDeleteReport: handleDeleteReport,
  };
}
